import React from 'react';
import {
  useQuery,
  getOrganizationBySlug,
  getEvents,
  getTenants,
  getLocationNotices,
  getFoodCourtItems,
  getFacilities,
  getServices,
  getTeamMembers,
} from 'wasp/client/operations';
import { REGULAR_EVENTS_LIST_FILTERS } from '../events/eventFilters';
import { useAuth } from 'wasp/client/auth';
import {
  Building2,
  Calendar,
  Users,
  Utensils,
  Megaphone,
  Zap,
} from 'lucide-react';
import { OpeningHoursPanel } from '../information-desk/OpeningHoursPanel';
import { HubTeamSection } from './HubTeamSection';
import {
  formatEventTime,
  isHappeningNow,
  SectionCard,
  ItemRow,
} from './hubOverviewShared';
import { HubTodayEvents } from './HubTodayEvents';

interface Props {
  slug: string;
  locationId?: string;
  children?: React.ReactNode;
}

export function OrgHubOverview({ slug, locationId: locationIdProp, children }: Props) {
  const { data: user } = useAuth();
  const isAdmin = !!user && (
    user.platformRole === 'ADMINISTRATOR' ||
    user.platformRole === 'PLATFORM_OWNER' ||
    user.platformRole === 'MODERATOR'
  );

  const { data: orgRaw, isLoading: orgLoading } = useQuery(
    getOrganizationBySlug,
    { slug },
    { enabled: !!slug }
  );
  const org = orgRaw as any;
  const orgId = org?.id;
  const enabled = { enabled: !!orgId };

  const { data: events = [] } = useQuery(
    getEvents,
    { organizationId: orgId, filters: REGULAR_EVENTS_LIST_FILTERS },
    enabled
  ) as { data: any[] };
  const { data: tenants = [] } = useQuery(getTenants, { organizationId: orgId }, enabled) as { data: any[] };
  const { data: notices = [] } = useQuery(getLocationNotices, { organizationId: orgId }, enabled) as { data: any[] };
  const { data: foodItems = [] } = useQuery(getFoodCourtItems, { organizationId: orgId }, enabled) as { data: any[] };
  const { data: facilities = [] } = useQuery(getFacilities, { organizationId: orgId }, enabled) as { data: any[] };
  const { data: services = [] } = useQuery(getServices, { organizationId: orgId }, enabled) as { data: any[] };

  const primaryLocationId: string | undefined =
    locationIdProp ?? org?.locationOrganizations?.[0]?.location?.id;
  const { data: teamMembers = [] } = useQuery(
    getTeamMembers,
    { locationId: primaryLocationId },
    { enabled: !!primaryLocationId }
  ) as { data: any[] };

  const base = `/${slug}`;

  const upcomingEvents = events
    .filter((e: any) => new Date(e.endTime) >= new Date())
    .sort((a: any, b: any) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
    .slice(0, 5);

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <Building2 className="w-10 h-10 mr-3" />
        <span>Organization not found.</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="hub-hero bg-gradient-to-br from-slate-50 via-blue-50 to-white rounded-2xl p-6 shadow-sm border border-slate-200/80">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold leading-tight text-gray-900 mb-2">{org.name}</h1>
            {org.description && (
              <p className="text-gray-600 text-sm leading-relaxed max-w-xl line-clamp-2">{org.description}</p>
            )}
          </div>

          {primaryLocationId && (
            <OpeningHoursPanel locationId={primaryLocationId} isAdmin={isAdmin} variant="hero" />
          )}
        </div>
      </div>

      {primaryLocationId && <HubTodayEvents locationId={primaryLocationId} orgSlug={slug} />}

      {children}

      <HubTeamSection members={teamMembers} accent="blue" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SectionCard
          title="Events & Activities"
          icon={<Calendar className="w-4 h-4" />}
          color="bg-blue-50 text-blue-700 border-blue-100"
          count={events.length}
          seeAllHref={`${base}/events`}
          isEmpty={upcomingEvents.length === 0}
          emptyLabel="No upcoming events."
        >
          <div className="space-y-0">
            {upcomingEvents.map((event: any) => (
              <div key={event.id} className="py-1.5 border-b border-gray-50 last:border-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-gray-800 truncate flex-1">{event.title}</p>
                  {isHappeningNow(event.startTime, event.endTime) && (
                    <span className="flex-shrink-0 flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Live
                    </span>
                  )}
                </div>
                <p className="text-xs text-blue-500 mt-0.5">{formatEventTime(event.startTime, event.endTime)}</p>
                {event.description && (
                  <p className="text-xs text-gray-400 truncate mt-0.5">{event.description}</p>
                )}
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title="Tenant Directory"
          icon={<Users className="w-4 h-4" />}
          color="bg-violet-50 text-violet-700 border-violet-100"
          count={tenants.length}
          seeAllHref={`${base}/tenants`}
          isEmpty={tenants.length === 0}
          emptyLabel="No tenants listed yet."
        >
          <div className="grid grid-cols-1 gap-1">
            {tenants.slice(0, 6).map((tenant: any) => (
              <div key={tenant.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
                <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-violet-600">
                    {(tenant.companyName ?? tenant.title ?? '?').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">{tenant.companyName ?? tenant.title}</p>
                  {tenant.category && (
                    <p className="text-xs text-gray-400 truncate">{tenant.category}</p>
                  )}
                </div>
              </div>
            ))}
            {tenants.length > 6 && (
              <p className="text-xs text-gray-400 pt-1">+{tenants.length - 6} more tenants</p>
            )}
          </div>
        </SectionCard>

        <SectionCard
          title="Food Court"
          icon={<Utensils className="w-4 h-4" />}
          color="bg-orange-50 text-orange-700 border-orange-100"
          count={foodItems.length}
          seeAllHref={`${base}/food-court`}
          isEmpty={foodItems.length === 0}
          emptyLabel="No food listings yet."
        >
          <div>
            {foodItems.slice(0, 5).map((item: any) => (
              <ItemRow key={item.id} label={item.title} sub={item.description} />
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SectionCard
          title="Facilities"
          icon={<Building2 className="w-4 h-4" />}
          color="bg-cyan-50 text-cyan-700 border-cyan-100"
          count={facilities.length}
          seeAllHref={`${base}/facilities`}
          isEmpty={facilities.length === 0}
          emptyLabel="No facilities listed."
        >
          {facilities.slice(0, 5).map((facility: any) => (
            <ItemRow key={facility.id} label={facility.title} sub={facility.description} badge={facility.category} />
          ))}
        </SectionCard>

        <SectionCard
          title="Services"
          icon={<Zap className="w-4 h-4" />}
          color="bg-emerald-50 text-emerald-700 border-emerald-100"
          count={services.length}
          seeAllHref={`${base}/services`}
          isEmpty={services.length === 0}
          emptyLabel="No services listed."
        >
          {services.slice(0, 5).map((service: any) => (
            <ItemRow key={service.id} label={service.title} sub={service.description} badge={service.category} />
          ))}
        </SectionCard>
      </div>
    </div>
  );
}
